import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import BrandLogo from '../common/BrandLogo';
import fondoDanes from '../../assets/fondo-danes.png';

// Sub-combo configurable (anidado)
const isConfigurable = (attr) => Array.isArray(attr.sub_attributes) && attr.sub_attributes.length > 0;

// Descuento por agrandado: cuenta el total de ítems pagados en categorías "es_agrandado"
// (ej: Tamaño Bebida + Tamaño Papa) y aplica el descuento del producto.
const calcAgrandadoDiscount = (quantities, customAttributes, categoryLimits, discountFrom, discountEach) => {
    if (!discountFrom || !discountEach || !customAttributes || !categoryLimits) return 0;
    let count = 0;
    customAttributes.forEach(attr => {
        const lim = categoryLimits[attr.category_id];
        if (!lim || !lim.es_agrandado) return;
        const qty = quantities[attr.id] || 0;
        const def = attr.is_default ? (attr.default_qty || 0) : 0;
        const paid = Math.max(0, qty - def);
        if (paid > 0 && (attr.price || 0) > 0) count += paid;
    });
    return discountEach * Math.max(0, count - (discountFrom - 1));
};
const subProductFor = (attr) => ({
    name: attr.name,
    price: 0,
    custom_attributes: attr.sub_attributes || [],
    category_limits: attr.sub_category_limits || {},
    agrandado_discount_from: attr.sub_agrandado_discount_from || 0,
    agrandado_discount_each: attr.sub_agrandado_discount_each || 0,
});

// Detección de tamaño por nombre (para "Agrandar todo"). Nivel de módulo = estable.
const SIZE_KEYS = [
    { key: 'pequeno', label: 'Pequeño', test: /peque/i },
    { key: 'mediano', label: 'Mediano', test: /median/i },
    { key: 'grande', label: 'Grande', test: /grande/i },
];
const sizeKeyOf = (name) => {
    const s = (name || '').toLowerCase();
    const f = SIZE_KEYS.find(k => k.test.test(s));
    if (f) return f.key;
    return /(normal|chic)/.test(s) ? 'normal' : null;
};

// Cantidad "por defecto" de la receta: si viene marcado por defecto usa su default_qty, si no 0.
const defaultQtyOf = (attr) => (attr && attr.is_default ? (attr.default_qty || 0) : 0);

const ProductOptionsModal = ({ product, onClose, onSubmit }) => {
    const [selectedQuantities, setSelectedQuantities] = useState({});
    const [validationErrors, setValidationErrors] = useState({});
    const [categoryErrors, setCategoryErrors] = useState({});
    const [currentStep, setCurrentStep] = useState(0);
    const [maxStepReached, setMaxStepReached] = useState(0); // paso más avanzado visitado (para navegar libre)
    const [subConfigs, setSubConfigs] = useState({});   // { [attr.id]: { selectedQuantities, subPrice, attribute_lines } }
    const [configuring, setConfiguring] = useState(null); // atributo en sub-configuración | null

    const groupedAttributes = useMemo(() => {
        if (!product.custom_attributes || product.custom_attributes.length === 0) {
            return {};
        }

        const groups = {};
        product.custom_attributes.forEach(attr => {
            const categoryName = attr.category_name || 'Sin Categoria';
            if (!groups[categoryName]) {
                groups[categoryName] = {
                    category_id: attr.category_id,
                    category_name: categoryName,
                    attributes: []
                };
            }
            groups[categoryName].attributes.push(attr);
        });

        return groups;
    }, [product.custom_attributes]);

    const categorySteps = useMemo(() => {
        // Orden fijo de los pasos: Pan primero → Agrandar segundo → resto → Ingredientes último.
        const limits = product.category_limits || {};
        const priority = (name, catId) => {
            const n = (name || '').trim().toLowerCase();
            if (n === 'pan') return 0;
            if (limits[catId]?.es_agrandado) return 1;
            if (n.startsWith('ingrediente')) return 9;
            return 5;
        };
        return Object.keys(groupedAttributes)
            .map((categoryName) => ({ categoryName, categoryData: groupedAttributes[categoryName] }))
            .sort((a, b) => priority(a.categoryName, a.categoryData.category_id) - priority(b.categoryName, b.categoryData.category_id))
            .map((step, index) => ({ ...step, index }));
    }, [groupedAttributes, product.category_limits]);

    const totalSteps = categorySteps.length + 1;

    const categoryLimits = useMemo(() => {
        return product.category_limits || {};
    }, [product.category_limits]);

    const calculateCategoryTotal = useCallback((categoryId) => {
        if (!product.custom_attributes) return 0;

        let total = 0;
        product.custom_attributes.forEach(attr => {
            if (attr.category_id === categoryId) {
                total += (selectedQuantities[attr.id] || 0);
            }
        });
        return total;
    }, [selectedQuantities, product.custom_attributes]);

    const getCategoryMinLimit = useCallback((categoryId) => {
        return categoryLimits[categoryId]?.min_quantity || 0;
    }, [categoryLimits]);

    const getCategoryMaxLimit = useCallback((categoryId) => {
        return categoryLimits[categoryId]?.max_quantity || 0;
    }, [categoryLimits]);

    const isCategoryLimitReached = useCallback((categoryId) => {
        const limit = getCategoryMaxLimit(categoryId);
        if (limit === 0) return false;
        return calculateCategoryTotal(categoryId) >= limit;
    }, [getCategoryMaxLimit, calculateCategoryTotal]);

    const allAttributesHaveMaxOne = useCallback((attributes) => {
        return attributes.every(attr => attr.max_qty === 1);
    }, []);

    const totalPrice = useMemo(() => {
        let price = product.price;

        if (product.custom_attributes && product.custom_attributes.length > 0) {
            product.custom_attributes.forEach(attr => {
                const qty = selectedQuantities[attr.id] || 0;
                const defaultQty = attr.is_default ? attr.default_qty : 0;
                const extraQty = Math.max(0, qty - defaultQty);

                if (extraQty > 0) {
                    price += attr.price * extraQty;
                }
                // Roll-up: extras del sub-combo configurado
                if (qty > 0 && subConfigs[attr.id]) {
                    price += subConfigs[attr.id].subPrice;
                }
            });
        }

        // Descuento por agrandado (0 si no hay regla)
        price -= calcAgrandadoDiscount(
            selectedQuantities, product.custom_attributes, product.category_limits,
            product.agrandado_discount_from, product.agrandado_discount_each
        );

        return Math.max(0, price);
    }, [selectedQuantities, product, subConfigs]);

    // --- "Agrandar todo": pone el mismo tamaño en todas las categorías de agrandado ---
    const isAgrandadoCat = useCallback((catId) => !!categoryLimits[catId]?.es_agrandado, [categoryLimits]);
    const categoryHasSize = useCallback((catId, sizeKey) =>
        (product.custom_attributes || []).some(a => a.category_id === catId && sizeKeyOf(a.name) === sizeKey),
        [product.custom_attributes]);
    const availableSizes = useMemo(() => {
        const present = new Set();
        (product.custom_attributes || []).forEach(attr => {
            if (categoryLimits[attr.category_id]?.es_agrandado) {
                const k = sizeKeyOf(attr.name);
                if (k && k !== 'normal') present.add(k);
            }
        });
        return SIZE_KEYS.filter(k => present.has(k.key));
    }, [product.custom_attributes, categoryLimits]);
    const setAllSize = useCallback((sizeKey) => {
        setSelectedQuantities(prev => {
            const next = { ...prev };
            (product.custom_attributes || []).forEach(attr => {
                if (categoryLimits[attr.category_id]?.es_agrandado && categoryHasSize(attr.category_id, sizeKey)) {
                    next[attr.id] = sizeKeyOf(attr.name) === sizeKey ? 1 : 0;
                }
            });
            return next;
        });
    }, [product.custom_attributes, categoryLimits, categoryHasSize]);
    const agrandarBtnStyle = { background: '#009542', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 18px', fontWeight: 800, fontSize: '0.95rem', cursor: 'pointer' };

    const openSubConfig = useCallback((attr) => setConfiguring(attr), []);

    const handleSubSubmit = useCallback((subQty, subTotal, subDesc, subAttrLines) => {
        setConfiguring(cfg => {
            if (cfg) {
                setSubConfigs(prev => ({
                    ...prev,
                    [cfg.id]: { selectedQuantities: subQty, subPrice: subTotal || 0, attribute_lines: subAttrLines || [], desc: subDesc || '' }
                }));
                setSelectedQuantities(prev => ({ ...prev, [cfg.id]: 1 }));
            }
            return null;
        });
    }, []);

    // Numeración de sub-combos repetidos dentro de una categoría (dúo/trío): "Compartir Normal 1/2".
    const displayName = (categoryData, attr) => {
        const same = (categoryData.attributes || []).filter(a => a.product_id === attr.product_id);
        return same.length > 1 ? `${attr.name} ${same.indexOf(attr) + 1}` : attr.name;
    };
    // ¿El sub-combo tiene selecciones OBLIGATORIAS (mín > 0) que hay que configurar?
    const nestedRequiresConfig = (attr) =>
        Object.values(attr.sub_category_limits || {}).some(l => (l.min_quantity || 0) > 0) ||
        (attr.sub_attributes || []).some(a => (a.min_qty || 0) > 0);
    const needsConfig = (attr) =>
        isConfigurable(attr) && (selectedQuantities[attr.id] || 0) > 0 && nestedRequiresConfig(attr) && !subConfigs[attr.id];
    const nestedValid = (product.custom_attributes || []).every(a => !needsConfig(a));

    useEffect(() => {
        const initialQuantities = {};

        if (product.custom_attributes && product.custom_attributes.length > 0) {
            product.custom_attributes.forEach(attr => {
                if (attr.is_default && attr.default_qty > 0) {
                    initialQuantities[attr.id] = attr.default_qty;
                } else {
                    initialQuantities[attr.id] = 0;
                }
            });
        }

        setSelectedQuantities(initialQuantities);
        setValidationErrors({});
        setCategoryErrors({});
    }, [product]);

    const validateCurrentStep = useCallback(() => {
        if (currentStep >= categorySteps.length) {
            return true;
        }

        const currentCategory = categorySteps[currentStep];
        const categoryData = currentCategory.categoryData;
        const errors = {};
        let hasErrors = false;

        const categoryTotal = calculateCategoryTotal(categoryData.category_id);
        const minRequired = getCategoryMinLimit(categoryData.category_id);

        if (minRequired > 0 && categoryTotal < minRequired) {
            setCategoryErrors(prev => ({
                ...prev,
                [categoryData.category_id]: `Debes seleccionar al menos ${minRequired} ${categoryData.category_name}`
            }));
            hasErrors = true;
        }

        categoryData.attributes.forEach(attr => {
            const qty = selectedQuantities[attr.id] || 0;

            if (qty > 0 && qty < attr.min_qty) {
                errors[attr.id] = `Mínimo ${attr.min_qty} unidades`;
                hasErrors = true;
            } else if (attr.max_qty > 0 && qty > attr.max_qty) {
                errors[attr.id] = `Máximo ${attr.max_qty} unidades`;
                hasErrors = true;
            }
        });

        setValidationErrors(errors);
        return !hasErrors;
    }, [currentStep, categorySteps, selectedQuantities, calculateCategoryTotal, getCategoryMinLimit]);

    const handleNextStep = useCallback(() => {
        if (!validateCurrentStep()) {
            return;
        }

        if (currentStep < totalSteps - 1) {
            setCurrentStep(prev => prev + 1);
            setValidationErrors({});
            setCategoryErrors({});
        }
    }, [currentStep, totalSteps, validateCurrentStep]);

    const handlePreviousStep = useCallback(() => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
            setValidationErrors({});
            setCategoryErrors({});
        }
    }, [currentStep]);

    // Registra el paso más avanzado alcanzado, para permitir saltar libre a cualquiera ya visitado.
    useEffect(() => { setMaxStepReached(m => Math.max(m, currentStep)); }, [currentStep]);

    const goToStep = useCallback((stepIndex) => {
        // Puedes ir a cualquier paso que YA hayas visitado (adelante o atrás), no solo hacia atrás.
        if (stepIndex <= maxStepReached) {
            setCurrentStep(stepIndex);
            setValidationErrors({});
            setCategoryErrors({});
        }
    }, [maxStepReached]);

    const handleSelectSingle = useCallback((attrId, categoryId, categoryData) => {
        setSelectedQuantities(prev => {
            const newQuantities = { ...prev };

            categoryData.attributes.forEach(attr => {
                newQuantities[attr.id] = 0;
            });

            newQuantities[attrId] = 1;

            return newQuantities;
        });

        setValidationErrors({});
        setCategoryErrors({});

        setTimeout(() => {
            setCurrentStep(prev => (prev < totalSteps - 1 ? prev + 1 : prev));
            setValidationErrors({});
            setCategoryErrors({});
        }, 300);
    }, [totalSteps]);

    const handleToggleSelection = useCallback((attrId, categoryId) => {
        setSelectedQuantities(prev => {
            const currentQty = prev[attrId] || 0;
            const newQty = currentQty === 0 ? 1 : 0;

            const categoryLimit = getCategoryMaxLimit(categoryId);
            if (categoryLimit > 0 && newQty === 1) {
                const currentCategoryTotal = calculateCategoryTotal(categoryId);
                if (currentCategoryTotal >= categoryLimit) {
                    const categoryData = groupedAttributes[Object.keys(groupedAttributes).find(
                        name => groupedAttributes[name].category_id === categoryId
                    )];
                    setCategoryErrors(prevErrors => ({
                        ...prevErrors,
                        [categoryId]: `Máximo ${categoryLimit} ${categoryData?.category_name || 'items'}`
                    }));
                    setTimeout(() => {
                        setCategoryErrors(prevErrors => {
                            const newErrors = { ...prevErrors };
                            delete newErrors[categoryId];
                            return newErrors;
                        });
                    }, 3000);
                    return prev;
                }
            }

            const newQuantities = {
                ...prev,
                [attrId]: newQty
            };

            let newCategoryTotal = 0;
            product.custom_attributes.forEach(attr => {
                if (attr.category_id === categoryId) {
                    newCategoryTotal += (newQuantities[attr.id] || 0);
                }
            });

            if (categoryLimit > 0 && newCategoryTotal >= categoryLimit) {
                setTimeout(() => {
                    setCurrentStep(prev => (prev < totalSteps - 1 ? prev + 1 : prev));
                    setValidationErrors({});
                    setCategoryErrors({});
                }, 300);
            }

            return newQuantities;
        });

        setValidationErrors(prev => {
            if (prev[attrId]) {
                const newErrors = { ...prev };
                delete newErrors[attrId];
                return newErrors;
            }
            return prev;
        });
    }, [getCategoryMaxLimit, calculateCategoryTotal, groupedAttributes, product.custom_attributes, currentStep, totalSteps]);

    const handleIncrement = useCallback((attrId, maxQty, categoryId) => {
        setSelectedQuantities(prev => {
            const currentQty = prev[attrId] || 0;

            if (maxQty > 0 && currentQty >= maxQty) {
                return prev;
            }

            const categoryLimit = getCategoryMaxLimit(categoryId);
            if (categoryLimit > 0) {
                const currentCategoryTotal = calculateCategoryTotal(categoryId);
                if (currentCategoryTotal >= categoryLimit) {
                    const categoryData = groupedAttributes[Object.keys(groupedAttributes).find(
                        name => groupedAttributes[name].category_id === categoryId
                    )];
                    setCategoryErrors(prevErrors => ({
                        ...prevErrors,
                        [categoryId]: `Máximo ${categoryLimit} ${categoryData?.category_name || 'items'}`
                    }));
                    setTimeout(() => {
                        setCategoryErrors(prevErrors => {
                            const newErrors = { ...prevErrors };
                            delete newErrors[categoryId];
                            return newErrors;
                        });
                    }, 3000);
                    return prev;
                }
            }

            return {
                ...prev,
                [attrId]: currentQty + 1
            };
        });

        setValidationErrors(prev => {
            if (prev[attrId]) {
                const newErrors = { ...prev };
                delete newErrors[attrId];
                return newErrors;
            }
            return prev;
        });
    }, [getCategoryMaxLimit, calculateCategoryTotal, groupedAttributes]);

    const handleDecrement = useCallback((attrId, minQty) => {
        setSelectedQuantities(prev => {
            const currentQty = prev[attrId] || 0;

            if (currentQty <= 0) {
                return prev;
            }

            const newQty = currentQty - 1;

            if (newQty < minQty) {
                return prev;
            }

            return {
                ...prev,
                [attrId]: newQty
            };
        });
    }, []);

    const validateAllCategories = useCallback(() => {
        const errors = {};
        let hasErrors = false;

        categorySteps.forEach(step => {
            const categoryData = step.categoryData;
            const categoryTotal = calculateCategoryTotal(categoryData.category_id);
            const minRequired = getCategoryMinLimit(categoryData.category_id);

            if (minRequired > 0 && categoryTotal < minRequired) {
                errors[categoryData.category_id] = `Debes seleccionar al menos ${minRequired} ${categoryData.category_name}`;
                hasErrors = true;
            }
        });

        setCategoryErrors(errors);
        return !hasErrors;
    }, [categorySteps, calculateCategoryTotal, getCategoryMinLimit]);

    const handleAddToCart = useCallback(() => {
        if (!validateAllCategories()) {
            return;
        }
        if (!nestedValid) {
            return; // falta configurar alguna unidad del pack
        }

        const attributesDescription = [];
        const attribute_lines = [];
        const nestedAttributes = {};

        // Numeración de unidades repetidas (dúo/trío) para la descripción.
        const unitCount = {}, running = {};
        (product.custom_attributes || []).forEach(a => {
            if (isConfigurable(a)) unitCount[a.product_id] = (unitCount[a.product_id] || 0) + 1;
        });

        if (product.custom_attributes && product.custom_attributes.length > 0) {
            product.custom_attributes.forEach(attr => {
                const qty = selectedQuantities[attr.id] || 0;
                const def = defaultQtyOf(attr);
                const sc = subConfigs[attr.id];
                // Descripción para el cliente: solo lo que se APARTA de la receta.
                if (qty > 0 && isConfigurable(attr) && sc) {
                    // Sub-combo (unidad del pack): "Compartir Normal 1: <cambios>"
                    running[attr.product_id] = (running[attr.product_id] || 0) + 1;
                    const num = (unitCount[attr.product_id] || 0) > 1 ? ` ${running[attr.product_id]}` : '';
                    const d = (sc.desc && sc.desc.trim()) ? sc.desc : 'receta original';
                    attributesDescription.push(`${attr.name}${num}: ${d}`);
                } else if (qty !== def) {
                    if (qty === 0) {
                        // "Sin X" solo tiene sentido si no es selección única (ahí el cambio se ve en la nueva opción)
                        if (getCategoryMaxLimit(attr.category_id) !== 1) attributesDescription.push(`Sin ${attr.name}`);
                    } else {
                        attributesDescription.push(`${attr.name} x${qty}`);
                    }
                }
                // Mismo modelo que el PDV: se incluyen los QUITADOS (qty 0 con default > 0)
                // para que el KDS muestre "SIN X", e is_addition marca lo que se agrega por
                // sobre la receta. El precio real lo reconstruye el proxy (base + is_addition);
                // OperateCardScreen calcula la base como residual, así que aquí van los datos crudos.
                if (qty > 0 || def > 0) {
                    const removed = qty === 0 && def > 0;
                    const lineObj = {
                        attribute_id: attr.id,
                        product_id: attr.product_id || null,
                        name: attr.name,
                        attribute_name: attr.category_name || '',
                        category_id: attr.category_id || null,
                        qty: qty,
                        default_qty: def,
                        price: attr.price || 0,
                        is_addition: qty > def,
                        is_removal: removed
                    };
                    // Sub-combo configurable: sus líneas ya vienen con is_addition/default_qty/is_removal
                    // correctos desde el modal anidado (mismo componente), se adjuntan tal cual.
                    // (sc ya está declarado arriba, en este mismo forEach.)
                    if (sc && Array.isArray(sc.attribute_lines) && sc.attribute_lines.length > 0) {
                        lineObj.sub_attribute_lines = sc.attribute_lines;
                        const subMap = {};
                        sc.attribute_lines.forEach(s => {
                            if ((s.qty || 0) > 0) subMap[s.attribute_id] = s.qty;
                        });
                        if (Object.keys(subMap).length) nestedAttributes[attr.id] = subMap;
                    }
                    attribute_lines.push(lineObj);
                }
            });
        }

        // Descuento por agrandado (ya restado en totalPrice; aquí solo se refleja para el KDS/boleta)
        const agDiscount = calcAgrandadoDiscount(
            selectedQuantities, product.custom_attributes, product.category_limits,
            product.agrandado_discount_from, product.agrandado_discount_each
        );
        if (agDiscount > 0) {
            attribute_lines.push({
                attribute_id: null,
                name: 'Descuento agrandado',
                attribute_name: 'Descuento',
                category_id: null,
                qty: 1,
                price: -agDiscount,
                is_addition: false
            });
        }

        onSubmit(
            selectedQuantities,
            totalPrice,
            attributesDescription.join(', '),
            attribute_lines,
            Object.keys(nestedAttributes).length ? nestedAttributes : null
        );
        onClose();
    }, [validateAllCategories, product, selectedQuantities, totalPrice, subConfigs, onSubmit, onClose, getCategoryMaxLimit, nestedValid]);

    const renderStepIndicator = () => {
        return (
            <div className="step-indicator">
                {Array.from({ length: totalSteps }).map((_, index) => (
                    <div
                        key={index}
                        className={`step-dot ${index < currentStep ? 'completed' : ''} ${index === currentStep ? 'active' : ''} ${index <= maxStepReached ? 'clickable' : ''}`}
                        onClick={() => index <= maxStepReached && goToStep(index)}
                    >
                        <div className="dot-circle">
                            {index < currentStep && (
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M16.6667 5L7.5 14.1667L3.33334 10" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                        </div>
                        <div className="step-label">
                            {index < categorySteps.length
                                ? categorySteps[index].categoryName
                                : 'Resumen'}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderCurrentStepContent = () => {
        if (currentStep >= categorySteps.length) {
            return renderSummaryStep();
        }

        const currentCategory = categorySteps[currentStep];
        const categoryData = currentCategory.categoryData;
        const minRequired = getCategoryMinLimit(categoryData.category_id);
        const maxLimit = getCategoryMaxLimit(categoryData.category_id);
        const categoryTotal = calculateCategoryTotal(categoryData.category_id);
        const isSingleSelection = maxLimit === 1;
        const allMaxOne = allAttributesHaveMaxOne(categoryData.attributes);
        const useSelectionMode = isSingleSelection || (maxLimit > 1 && allMaxOne);

        return (
            <div className="step-content">
                <div className="category-header-step">
                    <h3 className="category-title-step">
                        {categoryData.category_name}
                        {minRequired > 0 && <span className="required-badge">Obligatorio</span>}
                    </h3>
                    {maxLimit > 0 && !isSingleSelection && (
                        <div className="category-limit-info">
                            Seleccionados: {categoryTotal} / {maxLimit}
                            {minRequired > 0 && ` (Mínimo ${minRequired})`}
                        </div>
                    )}
                    {isSingleSelection && (
                        <div className="category-limit-info">
                            Selecciona una opción{minRequired > 0 && ' (Obligatorio)'}
                        </div>
                    )}
                    {!isSingleSelection && !maxLimit && minRequired > 0 && (
                        <div className="category-limit-info">
                            Mínimo {minRequired} requerido - Seleccionados: {categoryTotal}
                        </div>
                    )}
                </div>

                {isAgrandadoCat(categoryData.category_id) && availableSizes.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '4px 0 14px' }}>
                        <span style={{ fontWeight: 800, color: '#5a5142', fontSize: '1rem' }}>Agrandar todo:</span>
                        <button onClick={() => setAllSize('normal')} style={{ ...agrandarBtnStyle, background: '#6b6355' }}>Normal</button>
                        {availableSizes.map(s => (
                            <button key={s.key} onClick={() => setAllSize(s.key)} style={agrandarBtnStyle}>{s.label}</button>
                        ))}
                    </div>
                )}

                {categoryErrors[categoryData.category_id] && (
                    <div className="error-message">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></svg>
                        <span>{categoryErrors[categoryData.category_id]}</span>
                    </div>
                )}

                {isSingleSelection ? (
                    <div className="selection-grid">
                        {categoryData.attributes.map((attr) => {
                            const qty = selectedQuantities[attr.id] || 0;
                            const isSelected = qty > 0;
                            const hasError = validationErrors[attr.id];

                            return (
                                <div
                                    key={attr.id}
                                    className={`selection-card ${isSelected ? 'selected' : ''} ${hasError ? 'error' : ''}`}
                                    onClick={() => isConfigurable(attr) ? openSubConfig(attr) : handleSelectSingle(attr.id, categoryData.category_id, categoryData)}
                                >
                                    <div className="selection-radio">
                                        {isSelected && <div className="radio-dot"></div>}
                                    </div>
                                    <div className="selection-info">
                                        <h4 className="selection-name">{displayName(categoryData, attr)}</h4>
                                        {isConfigurable(attr) ? (
                                            <span className="selection-price">
                                                {subConfigs[attr.id]
                                                    ? `✓ Configurado${subConfigs[attr.id].subPrice > 0 ? ` +$${subConfigs[attr.id].subPrice.toLocaleString('es-CL')}` : ''}`
                                                    : (needsConfig(attr) ? 'Configurar (requerido) ›' : 'Toca para configurar ›')}
                                            </span>
                                        ) : attr.price > 0 && (
                                            <span className="selection-price">
                                                +${attr.price.toLocaleString('es-CL')}
                                            </span>
                                        )}
                                    </div>
                                    {hasError && (
                                        <div className="error-message-inline">
                                            <span>{hasError}</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : useSelectionMode ? (
                    <div className="selection-grid">
                        {categoryData.attributes.map((attr) => {
                            const qty = selectedQuantities[attr.id] || 0;
                            const isSelected = qty > 0;
                            const hasError = validationErrors[attr.id];

                            return (
                                <div
                                    key={attr.id}
                                    className={`selection-card checkbox-card ${isSelected ? 'selected' : ''} ${hasError ? 'error' : ''}`}
                                    onClick={() => isConfigurable(attr) ? openSubConfig(attr) : handleToggleSelection(attr.id, categoryData.category_id)}
                                >
                                    <div className="selection-checkbox">
                                        {isSelected && (
                                            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                                                <path d="M16.6667 5L7.5 14.1667L3.33334 10" stroke="#009542" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                    </div>
                                    <div className="selection-info">
                                        <h4 className="selection-name">{displayName(categoryData, attr)}</h4>
                                        {isConfigurable(attr) ? (
                                            <span className="selection-price">
                                                {subConfigs[attr.id]
                                                    ? `✓ Configurado${subConfigs[attr.id].subPrice > 0 ? ` +$${subConfigs[attr.id].subPrice.toLocaleString('es-CL')}` : ''}`
                                                    : (needsConfig(attr) ? 'Configurar (requerido) ›' : 'Toca para configurar ›')}
                                            </span>
                                        ) : attr.price > 0 && (
                                            <span className="selection-price">
                                                +${attr.price.toLocaleString('es-CL')}
                                            </span>
                                        )}
                                    </div>
                                    {hasError && (
                                        <div className="error-message-inline">
                                            <span>{hasError}</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="attributes-grid">
                        {categoryData.attributes.map((attr) => {
                            const qty = selectedQuantities[attr.id] || 0;
                            const hasError = validationErrors[attr.id];

                            // Sub-combo configurable (anidado): mostrar tarjeta para configurar, no el +/-
                            if (isConfigurable(attr)) {
                                const isSelected = qty > 0;
                                return (
                                    <div
                                        key={attr.id}
                                        className={`selection-card ${isSelected ? 'selected' : ''} ${hasError ? 'error' : ''}`}
                                        onClick={() => openSubConfig(attr)}
                                    >
                                        <div className="selection-radio">
                                            {isSelected && <div className="radio-dot"></div>}
                                        </div>
                                        <div className="selection-info">
                                            <h4 className="selection-name">{displayName(categoryData, attr)}</h4>
                                            <span className="selection-price">
                                                {subConfigs[attr.id]
                                                    ? `✓ Configurado${subConfigs[attr.id].subPrice > 0 ? ` +$${subConfigs[attr.id].subPrice.toLocaleString('es-CL')}` : ''}`
                                                    : (needsConfig(attr) ? 'Configurar (requerido) ›' : 'Toca para configurar ›')}
                                            </span>
                                        </div>
                                        {hasError && (
                                            <div className="error-message-inline">
                                                <span>{hasError}</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            const isMaxed = attr.max_qty > 0 && qty >= attr.max_qty;
                            const isLimitReached = isCategoryLimitReached(categoryData.category_id);
                            const canIncrement = !isMaxed && (!isLimitReached || qty > 0);

                            return (
                                <div key={attr.id} className={`attribute-card ${hasError ? 'error' : ''}`}>
                                    <div className="attribute-info">
                                        <h4 className="attribute-name">{attr.name}</h4>
                                        <div className="attribute-meta">
                                            {attr.price > 0 && (
                                                <span className="attribute-price">
                                                    +${attr.price.toLocaleString('es-CL')}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="quantity-controls">
                                        <button
                                            className="qty-btn minus"
                                            onClick={() => handleDecrement(attr.id, attr.min_qty)}
                                            disabled={qty <= 0 || qty <= attr.min_qty}
                                        >
                                            -
                                        </button>
                                        <div className="qty-display">
                                            <span className="qty-number">{qty}</span>
                                        </div>
                                        <button
                                            className="qty-btn plus"
                                            onClick={() => handleIncrement(attr.id, attr.max_qty, categoryData.category_id)}
                                            disabled={!canIncrement}
                                        >
                                            +
                                        </button>
                                    </div>

                                    {hasError && (
                                        <div className="error-message">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></svg>
                                            <span>{hasError}</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    const renderSummaryStep = () => {
        const selectedItems = [];

        if (product.custom_attributes && product.custom_attributes.length > 0) {
            product.custom_attributes.forEach(attr => {
                const qty = selectedQuantities[attr.id] || 0;
                const def = defaultQtyOf(attr);
                if (qty === def) return;                              // igual a la receta → no se muestra
                const removed = qty === 0;
                if (removed && getCategoryMaxLimit(attr.category_id) === 1) return; // swap en selección única
                selectedItems.push({
                    name: attr.name,
                    qty,
                    removed,
                    price: attr.price,
                    categoryName: attr.category_name
                });
            });
        }

        const groupedSummary = {};
        selectedItems.forEach(item => {
            if (!groupedSummary[item.categoryName]) {
                groupedSummary[item.categoryName] = [];
            }
            groupedSummary[item.categoryName].push(item);
        });

        const hasValidationErrors = Object.keys(categoryErrors).length > 0;

        return (
            <div className="summary-content">
                <h3 className="summary-title">Resumen de tu pedido</h3>

                {hasValidationErrors && (
                    <div className="summary-validation-errors">
                        {Object.entries(categoryErrors).map(([categoryId, error]) => (
                            <div key={categoryId} className="error-message">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></svg>
                                <span>{error}</span>
                            </div>
                        ))}
                    </div>
                )}

                <div className="summary-product">
                    <h4 className="summary-product-name">{product.name}</h4>
                    <div className="summary-product-price">${product.price.toLocaleString('es-CL')}</div>
                </div>

                {Object.keys(groupedSummary).length > 0 ? (
                    <div className="summary-customizations">
                        <h4 className="summary-subtitle">Personalizaciones:</h4>
                        {Object.entries(groupedSummary).map(([categoryName, items]) => (
                            <div key={categoryName} className="summary-category">
                                <div className="summary-category-name">{categoryName}</div>
                                {items.map((item, index) => (
                                    <div key={index} className="summary-item">
                                        <span className="summary-item-text">
                                            {item.removed ? `Sin ${item.name}` : `${item.name} x${item.qty}`}
                                        </span>
                                        {!item.removed && item.price > 0 && (
                                            <span className="summary-item-price">
                                                +${(item.price * item.qty).toLocaleString('es-CL')}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="summary-customizations">
                        <p style={{ textAlign: 'center', color: '#6b7280', fontWeight: 700, fontSize: '1.15rem', margin: 0 }}>
                            Receta original, sin cambios.
                        </p>
                    </div>
                )}

                <div className="summary-total">
                    <span className="summary-total-label">Total:</span>
                    <span className="summary-total-value">${totalPrice.toLocaleString('es-CL')}</span>
                </div>
            </div>
        );
    };

    const fullscreenContent = (
        <div className="fullscreen-container">
            <div className="fullscreen-header">
                <div className="header-pattern"></div>
                <div className="header-content">
                    <BrandLogo />
                    <h2 className="product-name">{product.name}</h2>
                    <button className="btn-close" onClick={onClose}>✕</button>
                </div>
            </div>

            <div className="fullscreen-body">
                {renderStepIndicator()}

                <div className="content-area">
                    {renderCurrentStepContent()}
                </div>
            </div>

            <div className="fullscreen-footer">
                <div className="footer-total">
                    <span className="footer-total-label">Total con adicionales</span>
                    <span className="footer-total-value">${totalPrice.toLocaleString('es-CL')}</span>
                </div>
                <div className="footer-actions">
                    <button className="btn-cancel" onClick={onClose}>
                        Cancelar
                    </button>

                    {currentStep > 0 && (
                        <button className="btn-back" onClick={handlePreviousStep}>
                            ← Volver
                        </button>
                    )}

                    {currentStep < categorySteps.length ? (
                        <button
                            className="btn-continue"
                            onClick={handleNextStep}
                        >
                            Continuar →
                        </button>
                    ) : (
                        <>
                            {!nestedValid && (
                                <span style={{ color: '#dc2626', fontWeight: 800, fontSize: '1rem', marginRight: 'auto' }}>
                                    Configura todas las unidades del combo
                                </span>
                            )}
                            <button
                                className="btn-add-to-cart"
                                onClick={handleAddToCart}
                                disabled={!nestedValid}
                                style={!nestedValid ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                            >
                                Agregar al Carrito
                            </button>
                        </>
                    )}
                </div>
            </div>

            <style>{`
                * {
                    box-sizing: border-box;
                }

                .fullscreen-container {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: linear-gradient(rgba(255,255,255,0.82), rgba(255,255,255,0.88)), url(${fondoDanes});
                    background-size: cover;
                    background-position: center;
                    background-repeat: no-repeat;
                    background-color: #ffffff;
                    z-index: 9999;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }

                .fullscreen-header {
                    background: linear-gradient(135deg, #009542 0%, #0aa64d 100%);
                    padding: 2rem;
                    position: relative;
                    overflow: hidden;
                    flex-shrink: 0;
                }

                .header-pattern {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: linear-gradient(135deg, #009542 0%, #0aa64d 100%);
                    opacity: 0.15;
                }

                .header-content {
                    position: relative;
                    z-index: 1;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 2rem;
                    max-width: 1400px;
                    margin: 0 auto;
                }

                .product-name {
                    color: white;
                    font-size: 2.5rem;
                    font-weight: 900;
                    margin: 0;
                    flex: 1;
                    text-align: center;
                    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
                }

                .btn-close {
                    width: 56px;
                    height: 56px;
                    border-radius: 12px;
                    border: none;
                    background: rgba(255, 255, 255, 0.2);
                    color: white;
                    font-size: 1.75rem;
                    cursor: pointer;
                    transition: all 200ms ease;
                    backdrop-filter: blur(10px);
                }

                .btn-close:hover {
                    background: rgba(255, 255, 255, 0.3);
                    transform: scale(1.05);
                }

                .fullscreen-body {
                    flex: 1;
                    display: flex;
                    overflow: hidden;
                }

                .step-indicator {
                    width: 300px;
                    background: #f9fafb;
                    padding: 2.5rem 2rem;
                    display: flex;
                    flex-direction: column;
                    gap: 2rem;
                    border-right: 3px solid #e5e7eb;
                    overflow-y: auto;
                    flex-shrink: 0;
                }

                .step-dot {
                    display: flex;
                    align-items: center;
                    gap: 1.25rem;
                    transition: all 200ms ease;
                    padding: 0.5rem;
                    border-radius: 12px;
                }

                .step-dot.clickable {
                    cursor: pointer;
                }

                .step-dot.clickable:hover {
                    background: rgba(24, 22, 19, 0.05);
                }

                .step-dot.clickable:hover .dot-circle {
                    transform: scale(1.1);
                }

                .dot-circle {
                    width: 50px;
                    height: 50px;
                    border-radius: 50%;
                    background: #d1d5db;
                    border: 4px solid #d1d5db;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 200ms ease;
                    flex-shrink: 0;
                }

                .step-dot.active .dot-circle {
                    background: #009542;
                    border-color: #009542;
                    box-shadow: 0 0 0 5px rgba(24, 22, 19, 0.15);
                }

                .step-dot.completed .dot-circle {
                    background: #10b981;
                    border-color: #10b981;
                }

                .step-label {
                    font-size: 1.1rem;
                    font-weight: 600;
                    color: #6b7280;
                    flex: 1;
                }

                .step-dot.active .step-label {
                    color: #009542;
                    font-weight: 800;
                }

                .step-dot.completed .step-label {
                    color: #10b981;
                }

                .content-area {
                    flex: 1;
                    padding: 3rem;
                    overflow-y: auto;
                }

                .step-content {
                    max-width: 1000px;
                    margin: 0 auto;
                    animation: fadeInSlide 400ms ease;
                }

                @keyframes fadeInSlide {
                    from {
                        opacity: 0;
                        transform: translateX(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }

                .category-header-step {
                    margin-bottom: 3rem;
                    padding-bottom: 1.5rem;
                    border-bottom: 4px solid #009542;
                }

                .category-title-step {
                    font-size: 2.5rem;
                    font-weight: 900;
                    color: #1f2937;
                    margin: 0 0 0.75rem 0;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }

                .required-badge {
                    background: #dc2626;
                    color: white;
                    padding: 0.5rem 1rem;
                    border-radius: 12px;
                    font-size: 1rem;
                    font-weight: 700;
                }

                .category-limit-info {
                    font-size: 1.125rem;
                    color: #6b7280;
                    font-weight: 600;
                }

                .selection-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
                    gap: 1.25rem;
                }

                .selection-card {
                    background: #ffffff;
                    border: 3px solid #009542;
                    border-radius: 16px;
                    padding: 1.5rem;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.75rem;
                    cursor: pointer;
                    transition: all 200ms ease;
                    min-height: 80px;
                    text-align: center;
                }

                .selection-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
                }

                .selection-card.selected {
                    background: #009542;
                    border-color: #0aa64d;
                    box-shadow: 0 6px 20px rgba(24, 22, 19, 0.3);
                }

                .selection-card.error {
                    border-color: #dc2626;
                    background: #fef2f2;
                }

                .selection-radio {
                    width: 28px;
                    height: 28px;
                    border-radius: 50%;
                    border: 3px solid #009542;
                    background: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    transition: all 200ms ease;
                }

                .selection-card.selected .selection-radio {
                    border-color: white;
                    background: white;
                }

                .radio-dot {
                    width: 14px;
                    height: 14px;
                    border-radius: 50%;
                    background: #009542;
                }

                .selection-checkbox {
                    width: 28px;
                    height: 28px;
                    border-radius: 6px;
                    border: 3px solid #009542;
                    background: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    transition: all 200ms ease;
                }

                .selection-card.checkbox-card.selected .selection-checkbox {
                    border-color: white;
                    background: white;
                }

                .selection-info {
                    flex: 1;
                    text-align: center;
                }

                .selection-name {
                    font-size: 1.125rem;
                    font-weight: 700;
                    color: #1f2937;
                    margin: 0 0 0.25rem 0;
                }

                .selection-card.selected .selection-name {
                    color: white;
                }

                .selection-price {
                    font-size: 1rem;
                    font-weight: 700;
                    color: #009542;
                    display: inline-block;
                }

                .selection-card.selected .selection-price {
                    color: white;
                }

                .error-message-inline {
                    width: 100%;
                    padding: 0.75rem;
                    background: #fef2f2;
                    border-radius: 8px;
                    color: #dc2626;
                    font-size: 0.875rem;
                    font-weight: 600;
                    margin-top: 0.75rem;
                }

                .attributes-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    gap: 1.25rem;
                }

                .attribute-card {
                    background: #ffffff;
                    border: 3px solid #009542;
                    border-radius: 16px;
                    padding: 1.25rem;
                    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
                    transition: all 200ms ease;
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                    text-align: center;
                    align-items: center;
                }

                .attribute-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15);
                }

                .attribute-card.error {
                    border: 3px solid #dc2626;
                    background: #fef2f2;
                }

                .attribute-info {
                    margin-bottom: 0;
                }

                .attribute-name {
                    font-size: 1.125rem;
                    font-weight: 700;
                    color: #1f2937;
                    margin: 0 0 0.25rem 0;
                }

                .attribute-meta {
                    display: flex;
                    gap: 0.5rem;
                    align-items: center;
                    justify-content: center;
                }

                .attribute-price {
                    font-size: 1rem;
                    font-weight: 700;
                    color: #009542;
                }

                .quantity-controls {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    justify-content: center;
                }

                .qty-btn {
                    width: 56px;
                    height: 56px;
                    border-radius: 14px;
                    border: none;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 200ms ease;
                    color: white;
                    font-size: 1.5rem;
                    font-weight: 700;
                }

                .qty-btn.minus {
                    background: #6b7280;
                }

                .qty-btn.plus {
                    background: #009542;
                }

                .qty-btn:disabled {
                    background: #d1d5db;
                    cursor: not-allowed;
                    opacity: 0.4;
                }

                .qty-btn:not(:disabled):hover {
                    transform: scale(1.1);
                }

                .qty-btn:not(:disabled):active {
                    transform: scale(0.95);
                }

                .qty-display {
                    min-width: 70px;
                    height: 56px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f3f4f6;
                    border-radius: 14px;
                }

                .qty-number {
                    font-size: 1.75rem;
                    font-weight: 900;
                    color: #009542;
                }

                .error-message {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 1rem;
                    background-color: #fef2f2;
                    border-left: 4px solid #dc2626;
                    border-radius: 8px;
                    color: #dc2626;
                    font-size: 1rem;
                    font-weight: 700;
                    margin-top: 1rem;
                }

                .summary-content {
                    max-width: 800px;
                    margin: 0 auto;
                    animation: fadeInSlide 400ms ease;
                }

                .summary-validation-errors {
                    margin-bottom: 2rem;
                }

                .summary-title {
                    font-size: 2.5rem;
                    font-weight: 900;
                    color: #1f2937;
                    margin: 0 0 2.5rem 0;
                    text-align: center;
                }

                .summary-product {
                    background: linear-gradient(135deg, #009542 0%, #0aa64d 100%);
                    border-radius: 20px;
                    padding: 2rem;
                    margin-bottom: 2.5rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .summary-product-name {
                    font-size: 1.75rem;
                    font-weight: 800;
                    color: white;
                    margin: 0;
                }

                .summary-product-price {
                    font-size: 2rem;
                    font-weight: 900;
                    color: white;
                }

                .summary-customizations {
                    background: #f9fafb;
                    border-radius: 20px;
                    padding: 2rem;
                    margin-bottom: 2.5rem;
                }

                .summary-subtitle {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #1f2937;
                    margin: 0 0 1.5rem 0;
                }

                .summary-category {
                    margin-bottom: 2rem;
                }

                .summary-category:last-child {
                    margin-bottom: 0;
                }

                .summary-category-name {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: #009542;
                    margin-bottom: 1rem;
                    padding-bottom: 0.75rem;
                    border-bottom: 2px solid #fee2e2;
                }

                .summary-item {
                    display: flex;
                    justify-content: space-between;
                    padding: 0.625rem 0;
                    color: #374151;
                }

                .summary-item-text {
                    font-size: 1.125rem;
                    font-weight: 600;
                }

                .summary-item-price {
                    font-size: 1.125rem;
                    font-weight: 700;
                    color: #009542;
                }

                .summary-total {
                    background: #1f2937;
                    border-radius: 20px;
                    padding: 2rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .summary-total-label {
                    font-size: 1.75rem;
                    font-weight: 700;
                    color: white;
                }

                .summary-total-value {
                    font-size: 3rem;
                    font-weight: 900;
                    color: #ffffff;
                }

                .fullscreen-footer {
                    background: white;
                    padding: 1.75rem 2rem;
                    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
                    border-top: 3px solid #e5e7eb;
                    flex-shrink: 0;
                }

                .footer-total {
                    display: flex;
                    align-items: baseline;
                    justify-content: flex-end;
                    gap: 0.9rem;
                    max-width: 1400px;
                    margin: 0 auto 1rem;
                }

                .footer-total-label {
                    font-size: 1.15rem;
                    font-weight: 700;
                    color: #6b7280;
                }

                .footer-total-value {
                    font-size: 2rem;
                    font-weight: 900;
                    color: #009542;
                }

                .footer-actions {
                    display: flex;
                    gap: 1.25rem;
                    max-width: 1400px;
                    margin: 0 auto;
                    align-items: center;
                    justify-content: flex-end;
                }

                .btn-cancel {
                    padding: 1.25rem 2rem;
                    font-size: 1.125rem;
                    font-weight: 700;
                    border: 3px solid #6b7280;
                    border-radius: 16px;
                    cursor: pointer;
                    transition: all 200ms ease;
                    background: white;
                    color: #6b7280;
                }

                .btn-cancel:hover {
                    background: #6b7280;
                    color: white;
                    transform: translateY(-2px);
                }

                .btn-back {
                    padding: 1.25rem 2rem;
                    font-size: 1.125rem;
                    font-weight: 700;
                    border: none;
                    border-radius: 16px;
                    cursor: pointer;
                    transition: all 200ms ease;
                    background: #6b7280;
                    color: white;
                }

                .btn-back:hover {
                    background: #4b5563;
                    transform: translateY(-2px);
                }

                .btn-continue {
                    padding: 1.25rem 2.5rem;
                    font-size: 1.125rem;
                    font-weight: 700;
                    border: none;
                    border-radius: 16px;
                    cursor: pointer;
                    transition: all 200ms ease;
                    background: #009542;
                    color: white;
                }

                .btn-continue:hover {
                    background: #0aa64d;
                    transform: translateY(-2px);
                }

                .btn-add-to-cart {
                    padding: 1.5rem 3rem;
                    font-size: 1.375rem;
                    font-weight: 800;
                    border: none;
                    border-radius: 16px;
                    cursor: pointer;
                    transition: all 200ms ease;
                    background: #009542;
                    color: white;
                    box-shadow: 0 4px 12px rgba(24, 22, 19, 0.3);
                }

                .btn-add-to-cart:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 20px rgba(24, 22, 19, 0.4);
                }

                @media (max-width: 1024px) {
                    .step-indicator {
                        width: 100%;
                        flex-direction: row;
                        overflow-x: auto;
                        border-right: none;
                        border-bottom: 3px solid #e5e7eb;
                        padding: 1.5rem;
                    }

                    .fullscreen-body {
                        flex-direction: column;
                    }

                    .step-dot {
                        flex-direction: column;
                        min-width: 110px;
                        text-align: center;
                    }

                    .step-label {
                        font-size: 0.9rem;
                    }

                    .dot-circle {
                        width: 42px;
                        height: 42px;
                    }
                }

                @media (max-width: 768px) {
                    .product-name {
                        font-size: 1.75rem;
                    }

                    .header-content {
                        flex-direction: column;
                        gap: 1rem;
                    }

                    .content-area {
                        padding: 1.5rem;
                    }

                    .footer-actions {
                        flex-wrap: wrap;
                    }

                    .btn-back,
                    .btn-continue,
                    .btn-add-to-cart {
                        margin-left: 0;
                        flex: 1;
                    }

                    .selection-grid {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );

    // Si hay un sub-combo en configuración, mostrar su modal (recursivo, máx 2 niveles)
    if (configuring) {
        return (
            <ProductOptionsModal
                product={subProductFor(configuring)}
                onClose={() => setConfiguring(null)}
                onSubmit={handleSubSubmit}
            />
        );
    }

    return createPortal(fullscreenContent, document.body);
};

export default ProductOptionsModal;